import type { LayoutType } from '../../shared/types/LayoutType';
import type { ThemePreference, ThemeSchedule, ResolvedTheme } from '../../shared/types/ThemeType';
import type { AccentType } from '../../shared/types/AccentType';
import type { LanguageCode } from '../../shared/types/LanguageType';
import type { RemoveBgStatsView } from '../../shared/types/removeBgStats';

export type RootFolderSettings = {
  mode: ThemePreference;
  currentLang: string;
  isInitialized: boolean;
  accent: 'brick' | 'sakura' | 'emerald' | 'royal';
  themeSchedule?: ThemeSchedule;
};

export type RootFolder = {
  folderId: string;
  folderName: string;
  settings: RootFolderSettings;
  isActive: boolean;
};

export type SharedFolder = {
  folderId: string;
  folderName: string;
  role: 'admin' | 'editor' | 'viewer';
  settings: RootFolderSettings;
};

export type UserStats = {
  usedSize: number;
  usedFolders: number;
  usedRemoveBg?: number;
  limits: {
    maxTotalSize: number;
    maxRootFolders: number;
    maxRemoveBg?: number;
  };
  removeBg?: RemoveBgStatsView;
};

export type UserData = {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  isActive?: boolean | null;
  emailVerified?: boolean | null;
  emailVerifiedAt?: string | Date | null;
  lastLogin?: string | null;
  rootFolders?: RootFolder[];
  sharedFolders?: SharedFolder[];
  stats?: UserStats;
  isAdmin?: boolean;
  onboardingCompletedAt?: string | null;
};

export type UserResponse = UserData;

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';
export type FolderType = 'own' | 'shared';

export const createEmptyUser = (): UserData => ({
  id: null,
  name: '',
  email: '',
  isActive: null,
  emailVerified: false,
  emailVerifiedAt: null,
  lastLogin: null,
  rootFolders: [],
  sharedFolders: [],
  stats: {
    usedSize: 0,
    usedFolders: 0,
    limits: {
      maxTotalSize: 0,
      maxRootFolders: 0,
    },
  },
});

export type AuthState = {
  user: UserData;
};

export type WorkspaceState = {
  currentFolderId: string | null;
  folderType: FolderType | null;
  role: WorkspaceRole;
};

export type UiState = {
  mode: ResolvedTheme;
  themePreference: ThemePreference;
  themeSchedule?: ThemeSchedule;
  accent: AccentType;
  isAuto: boolean;
  layout: {
    type: LayoutType;
    previousType: LayoutType | '';
  };
  sidebarCollapsed: boolean;
  currentLang: LanguageCode;
  isInitialized: boolean;
};

export type AppState = AuthState &
  WorkspaceState &
  UiState & {
    setUser: (payload: UserResponse) => void;
    setLang: (lang: LanguageCode) => void;
    initializeLang: (lang: LanguageCode) => void;
    setIsAuto: (payload: boolean) => void;
    setAccent: (accent: AccentType) => void;
    setMode: (payload: ResolvedTheme) => void;
    setThemePreference: (payload: ThemePreference) => void;
  setLayout: (payload: LayoutType) => void;
  setPreviousLayout: (payload: LayoutType | '') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
    setCurrentFolder: (folderId: string, folderType?: FolderType) => void;
    getCurrentFolder: () => (RootFolder | SharedFolder) | undefined;
    getCurrentFolderSettings: () => RootFolderSettings | undefined;
    reset: () => void;
  };
